import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import SingleDropDown, {
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import React, {
  ChangeEvent,
  ReactElement,
  useCallback,
  useMemo,
  useState,
} from 'react'
import ActionRequiredImage from 'src/assets/images/AccountVerificationRequired.svg'
import SvgChevronLeft from 'src/components/Svgs/SvgChevronLeft'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import { getLocaleInfo } from 'src/localization/locales'
import { useTranslate } from 'src/localization/Trns'
import { CountryListModel } from 'src/models/country.models'

import style from './SanctionedScreeningForm.style'

export interface PageNameFlatFields {
  firstName?: string
  lastName?: string
  phone?: string
}

export interface PageFirstFlatFields {
  birthday?: string
}

export interface PageSecondFlatFields {
  country?: string
}

export type RegisterAccountCountryFields = {
  address1?: string
  address2?: string
  city?: string
  province?: string
  postal?: string
  country?: string
}

export type AllScreeningFields = PageNameFlatFields &
  PageFirstFlatFields &
  PageSecondFlatFields &
  RegisterAccountCountryFields

interface SanctionedScreeningFormProps {
  issuerName: string
  topLevelError?: string
  appName: React.ReactElement
  includeFirstAndLastName?: boolean
  countryOptions: { value: string; label: string }[]
  countrysMap: Record<string, CountryListModel>
  onSubmit: (fields: AllScreeningFields) => void
  isLoading: boolean
  firstName?: string
  lastName?: string
  locale?: string
  phone?: string
  onBeforeReview?: (
    address: RegisterAccountCountryFields,
  ) => Promise<RegisterAccountCountryFields | null>
}

export default function SanctionedScreeningForm(
  props: SanctionedScreeningFormProps,
): ReactElement {
  const {
    issuerName,
    topLevelError,
    appName,
    includeFirstAndLastName,
    countryOptions,
    countrysMap,
    onSubmit,
    isLoading,
    firstName: initialFirstName,
    lastName: initialLastName,
    locale,
    phone: initialPhone,
    onBeforeReview,
  } = props

  const translate = useTranslate()
  const localeInfo = getLocaleInfo(locale || 'en-US')

  const [currentPage, setCurrentPage] = useState(
    includeFirstAndLastName ? 0 : 1,
  )
  const [isReviewLoading, setIsReviewLoading] = useState(false)

  // Page 0 state (Name)
  const [nameFields, setNameFields] = useState({
    firstName: initialFirstName || '',
    lastName: initialLastName || '',
    phone: initialPhone || '',
  })
  const [nameErrors, setNameErrors] = useState<
    Record<string, string[] | undefined>
  >({})

  // Page 1 state (Birthday)
  const [birthday, setBirthday] = useState('')
  const [birthdayError, setBirthdayError] = useState<string[] | undefined>()

  // Page 2 state (Address)
  const [country, setCountry] = useState('US')
  const [addressFields, setAddressFields] = useState<Record<string, string>>({
    address1: '',
    address2: '',
    city: '',
    province: '',
    postal: '',
  })
  const [addressErrors, setAddressErrors] = useState<
    Record<string, string[] | undefined>
  >({})

  // Page 0 handlers
  const handleNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget
    setNameFields((prev) => ({ ...prev, [name]: value }))
    setNameErrors((prev) => ({ ...prev, [name]: undefined }))
  }, [])

  const handleNameNext = useCallback(() => {
    const errors: Record<string, string[]> = {}
    if (!nameFields.firstName?.trim()) {
      errors.firstName = [translate('Must not be blank')]
    }
    if (!nameFields.lastName?.trim()) {
      errors.lastName = [translate('Must not be blank')]
    }
    if (
      !nameFields.phone?.match(
        /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      )
    ) {
      errors.phone = [translate('Must be a valid phone number')]
    }
    if (Object.keys(errors).length > 0) {
      setNameErrors(errors)
      return
    }
    setCurrentPage(1)
  }, [nameFields, translate])

  // Page 1 handlers
  const dateSegmentLengths = useMemo(() => {
    const segments = localeInfo.dateFormat.split('/')
    return [segments[0].length, segments[1].length, segments[2].length]
  }, [localeInfo.dateFormat])

  const handleBirthdayChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const digits = e.currentTarget.value.replace(/[^\d]/g, '')
      const [len0, len1, len2] = dateSegmentLengths
      let formatted: string
      if (digits.length <= len0) {
        formatted = digits
      } else if (digits.length <= len0 + len1) {
        formatted = `${digits.slice(0, len0)}/${digits.slice(len0)}`
      } else {
        formatted = `${digits.slice(0, len0)}/${digits.slice(len0, len0 + len1)}/${digits.slice(len0 + len1, len0 + len1 + len2)}`
      }
      setBirthday(formatted)
      setBirthdayError(undefined)
    },
    [dateSegmentLengths],
  )

  const handleBirthdayNext = useCallback(() => {
    const trimmedBirthday = birthday?.trim() ?? ''
    if (!trimmedBirthday) {
      setBirthdayError([translate('Must not be blank')])
      return
    }
    const parts = trimmedBirthday.split('/').filter(Boolean)
    if (parts.length !== 3) {
      setBirthdayError([translate('Must be a complete date')])
      return
    }
    const hasCorrectLengths = parts.every(
      (part, i) => part.length === dateSegmentLengths[i],
    )
    if (!hasCorrectLengths) {
      setBirthdayError([
        translate('Must be of the form ${dateFormat}', {
          dateFormat: localeInfo.dateFormat,
        }),
      ])
      return
    }
    const isoString = localeInfo.dateString(parts)
    if (isNaN(Date.parse(isoString))) {
      setBirthdayError([
        translate('Must be of the form ${dateFormat}', {
          dateFormat: localeInfo.dateFormat,
        }),
      ])
      return
    }
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    if (new Date(isoString) >= today) {
      setBirthdayError([translate('Date of birth must be in the past')])
      return
    }
    setBirthdayError(undefined)
    setCurrentPage(2)
  }, [birthday, dateSegmentLengths, localeInfo, translate])

  // Page 2 handlers
  const handleAddressFieldChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.currentTarget
      setAddressFields((prev) => ({ ...prev, [name]: value }))
      setAddressErrors((prev) => ({ ...prev, [name]: undefined }))
    },
    [],
  )

  const handleProvinceChange = useCallback(({ value }: DropdownValue) => {
    setAddressFields((prev) => ({ ...prev, province: value }))
    setAddressErrors((prev) => ({ ...prev, province: undefined }))
  }, [])

  const handleAddressNext = useCallback(async () => {
    const errors: Record<string, string[]> = {}
    countrysMap[country]?.fields?.forEach((field) => {
      if (!field.optional && !addressFields[field.name]?.trim()) {
        errors[field.name] = [translate('Must not be blank')]
      } else if (!field.optional && field.format) {
        const regex = new RegExp(field.format)
        if (!regex.test(addressFields[field.name] || '')) {
          errors[field.name] = [translate('Invalid value')]
        }
      }
    })
    // City is always required
    if (!addressFields.city?.trim()) {
      errors.city = [translate('Must not be blank')]
    }

    if (Object.keys(errors).length > 0) {
      setAddressErrors(errors)
      return
    }

    if (onBeforeReview) {
      setIsReviewLoading(true)
      try {
        const result = await onBeforeReview({
          address1: addressFields.address1,
          address2: addressFields.address2,
          city: addressFields.city,
          province: addressFields.province,
          postal: addressFields.postal,
          country,
        })
        if (result === null) {
          // User cancelled (clicked "Edit Address" on suggestion modal)
          return
        }
        // Replace form fields with the standardized address from validation.
        // Use ?? (not ||) so that empty strings from the API are kept as-is
        // rather than falling back to the user's original input.
        setAddressFields((prev) => ({
          address1: result.address1 ?? prev.address1,
          address2: result.address2 ?? prev.address2,
          city: result.city ?? prev.city,
          province: result.province ?? prev.province,
          postal: result.postal ?? prev.postal,
        }))
      } finally {
        setIsReviewLoading(false)
      }
    }

    setCurrentPage(3)
  }, [addressFields, country, countrysMap, onBeforeReview, translate])

  // Page 3 (Review) submit
  const handleSubmit = useCallback(() => {
    const allFields: AllScreeningFields = {
      ...(includeFirstAndLastName ? nameFields : {}),
      birthday,
      country,
      ...addressFields,
    }
    onSubmit(allFields)
  }, [
    includeFirstAndLastName,
    nameFields,
    birthday,
    country,
    addressFields,
    onSubmit,
  ])

  const formatReviewAddress = useCallback(() => {
    const parts: string[] = []
    if (addressFields.address1) parts.push(addressFields.address1)
    if (addressFields.address2) parts.push(addressFields.address2)
    const cityProvPostal = [
      addressFields.city,
      addressFields.province,
      addressFields.postal,
    ]
      .filter(Boolean)
      .join(', ')
    if (cityProvPostal) parts.push(cityProvPostal)
    const countryLabel = countryOptions.find((c) => c.value === country)?.label
    if (countryLabel) parts.push(countryLabel)
    return parts.join('\n')
  }, [addressFields, country, countryOptions])

  const getAddressFieldLabel = (name: string, defaultLabel: string): string => {
    if (name === 'address1') return translate('Home Address 1')
    if (name === 'address2') return translate('Home Address 2')
    return defaultLabel
  }

  // Shared UI pieces
  const renderScreeningImage = () => (
    <div className="screening-image-container">
      <img
        className="screening-image"
        src={ActionRequiredImage}
        alt=""
        role="presentation"
      />
    </div>
  )

  // Split the info-safe text around ${appName} since appName is a ReactElement
  // and translate() only accepts string expressions
  const infoSafeSource =
    'Your information is secure. We neither sell nor publicize your information outside of ${appName}.'
  const infoSafeParts = translate(infoSafeSource, {
    appName: '\u0000',
  }).split('\u0000')

  const renderInfoSafe = () => (
    <div className="info-safe" data-testid="sanctions-screening-info-safe">
      <SvgLocked
        viewBox="0 0 24 24"
        width={20}
        height={20}
        aria-hidden="true"
      />
      <span className="info-safe-text">
        {infoSafeParts[0]}
        {appName}
        {infoSafeParts[1]}
      </span>
    </div>
  )

  // Page 0: Name
  const renderNamePage = () => (
    <div className="screening-page" data-testid="sanctions-screening-page-name">
      <p className="screening-header">{translate('We need some info')}</p>
      <p className="screening-description">
        {translate(
          "We'll use this information to verify your identity, a crucial step in protecting both you and the primary card holder.",
        )}
      </p>
      <div className="form-fields">
        <ExtUITextInput
          name="firstName"
          testId="sanctions-screening-first-name"
          value={nameFields.firstName}
          onChange={handleNameChange}
          placeholder={translate('Enter first name')}
          label={translate('First Name')}
          errorText={nameErrors.firstName}
        />
        <ExtUITextInput
          name="lastName"
          testId="sanctions-screening-last-name"
          value={nameFields.lastName}
          onChange={handleNameChange}
          placeholder={translate('Enter last name')}
          label={translate('Last Name')}
          errorText={nameErrors.lastName}
        />
        <ExtUITextInput
          name="phone"
          testId="sanctions-screening-phone"
          value={nameFields.phone}
          onChange={handleNameChange}
          placeholder={translate('Enter phone number')}
          label={translate('Phone Number')}
          errorText={nameErrors.phone}
        />
      </div>
      <div className="form-bottom">
        <div />
        <Button
          data-testid="sanctions-screening-proceed-to-dob-btn"
          onClick={handleNameNext}
        >
          {translate('Proceed to Date of Birth')}
        </Button>
      </div>
      {renderInfoSafe()}
    </div>
  )

  // Page 1: Birthday
  const renderBirthdayPage = () => (
    <div className="screening-page" data-testid="sanctions-screening-page-dob">
      <p className="screening-header">{translate('We need some info')}</p>
      <p className="screening-description">
        {translate(
          'To continue using your ${issuerName} virtual card, we need you to ${confirm}. This information is collected to protect the account security of you and the primary card holder.',
          {
            issuerName: <strong>{issuerName}</strong>,
            confirm: (
              <strong>
                {translate('confirm your home address and date of birth')}
              </strong>
            ),
          },
        )}
      </p>
      <div className="form-fields">
        <ExtUITextInput
          name="birthday"
          testId="sanctions-screening-dob"
          value={birthday}
          onChange={handleBirthdayChange}
          placeholder={localeInfo.dateFormat}
          label={translate('Date of Birth')}
          errorText={birthdayError}
        />
      </div>
      <div className="form-bottom">
        {includeFirstAndLastName ? (
          <button
            type="button"
            className="back-link"
            data-testid="sanctions-screening-back-to-name-link"
            onClick={() => setCurrentPage(0)}
            aria-label={translate('Back to name')}
          >
            <SvgChevronLeft
              viewBox="0 0 24 24"
              width={18}
              height={18}
              aria-hidden="true"
            />
            {translate('Name')}
          </button>
        ) : (
          <div />
        )}
        <Button
          data-testid="sanctions-screening-continue-to-address-btn"
          onClick={handleBirthdayNext}
        >
          {translate('Continue to Home Address')}
        </Button>
      </div>
      {renderInfoSafe()}
    </div>
  )

  // Page 2: Address
  const renderAddressPage = () => (
    <div
      className="screening-page"
      data-testid="sanctions-screening-page-address"
    >
      <p className="screening-header">{translate('We need some info')}</p>
      <p className="screening-description">
        {translate(
          'Great, thank you. Next, please enter your physical home address.',
        )}
      </p>
      <div className="form-fields">
        <SingleDropDown
          selectedValue={countryOptions.find((opt) => opt.value === country)}
          label={translate('Country')}
          options={countryOptions}
          placeholder={translate('Select Country')}
          searchable
          onSelect={({ value }) => {
            setCountry(value)
            setAddressFields((prev) => ({ ...prev, province: '' }))
          }}
          autoComplete="country"
          data-testid="sanctions-screening-country-dropdown"
        />
        {country &&
          countrysMap[country]?.fields?.map((countryField) => {
            const { options, label, name } = countryField
            if (options) {
              const selectedOption = options.find(
                (opt) => opt.code === addressFields.province,
              )
              return (
                <SingleDropDown
                  key={name}
                  data-testid={`sanctions-screening-${name}-dropdown`}
                  selectedValue={
                    selectedOption
                      ? {
                          label: selectedOption.label,
                          value: selectedOption.code,
                        }
                      : undefined
                  }
                  label={label}
                  options={options.map((opt) => ({
                    value: opt.code,
                    label: opt.label,
                  }))}
                  searchable
                  onSelect={handleProvinceChange}
                  errorText={addressErrors.province}
                  placeholder={translate('Select ${fieldLabel}', {
                    fieldLabel: label,
                  })}
                  autoComplete="address-level1"
                />
              )
            }

            const fieldLabel = getAddressFieldLabel(name, label)
            return (
              <ExtUITextInput
                key={name}
                name={name}
                testId={`sanctions-screening-${name}`}
                onChange={handleAddressFieldChange}
                value={addressFields[name] || ''}
                placeholder={translate('Enter ${fieldLabel}', {
                  fieldLabel: fieldLabel,
                })}
                label={fieldLabel}
                errorText={addressErrors[name]}
              />
            )
          })}
      </div>
      {topLevelError && (
        <p
          className="top-level-error"
          data-testid="sanctions-screening-address-error"
          role="alert"
        >
          {topLevelError}
        </p>
      )}
      <div className="form-bottom">
        <button
          type="button"
          className="back-link"
          data-testid="sanctions-screening-back-to-dob-link"
          onClick={() => setCurrentPage(1)}
          aria-label={translate('Back to date of birth')}
        >
          <SvgChevronLeft
            viewBox="0 0 24 24"
            width={18}
            height={18}
            aria-hidden="true"
          />
          {translate('Date of Birth')}
        </button>
        <Button
          data-testid="sanctions-screening-review-and-confirm-btn"
          onClick={handleAddressNext}
          loading={isReviewLoading}
        >
          {translate('Review and Confirm')}
        </Button>
      </div>
      {renderInfoSafe()}
    </div>
  )

  // Page 3: Review
  const renderReviewPage = () => (
    <div
      className="screening-page"
      data-testid="sanctions-screening-page-review"
    >
      <p className="screening-header">{translate('Review and confirm')}</p>
      <p className="screening-description">
        {translate(
          'Before confirming your date of birth and home address, please review the information you entered and ensure it is correct.',
        )}
      </p>

      {includeFirstAndLastName && (
        <div
          className="review-box"
          data-testid="sanctions-screening-review-name-box"
        >
          <div className="review-box-top">
            <span className="review-box-title">{translate('Full Name')}</span>
            <button
              type="button"
              className="update-link"
              data-testid="sanctions-screening-update-name-link"
              onClick={() => setCurrentPage(0)}
              aria-label={translate('Update name')}
            >
              <SvgUpdate
                viewBox="0 0 24 24"
                width={16}
                height={16}
                aria-hidden="true"
              />
              {translate('Update')}
            </button>
          </div>
          <p className="review-box-content">
            {`${nameFields.firstName} ${nameFields.lastName}`}
            {nameFields.phone ? `\n${nameFields.phone}` : ''}
          </p>
        </div>
      )}

      <div
        className="review-box"
        data-testid="sanctions-screening-review-dob-box"
      >
        <div className="review-box-top">
          <span className="review-box-title">{translate('Date of Birth')}</span>
          <button
            type="button"
            className="update-link"
            data-testid="sanctions-screening-update-dob-link"
            onClick={() => setCurrentPage(1)}
            aria-label={translate('Update date of birth')}
          >
            <SvgUpdate
              viewBox="0 0 24 24"
              width={16}
              height={16}
              aria-hidden="true"
            />
            {translate('Update')}
          </button>
        </div>
        <p className="review-box-content">{birthday}</p>
      </div>

      <div
        className="review-box"
        data-testid="sanctions-screening-review-address-box"
      >
        <div className="review-box-top">
          <span className="review-box-title">{translate('Home Address')}</span>
          <button
            type="button"
            className="update-link"
            data-testid="sanctions-screening-update-address-link"
            onClick={() => setCurrentPage(2)}
            aria-label={translate('Update home address')}
          >
            <SvgUpdate
              viewBox="0 0 24 24"
              width={16}
              height={16}
              aria-hidden="true"
            />
            {translate('Update')}
          </button>
        </div>
        <p className="review-box-content">{formatReviewAddress()}</p>
      </div>

      {topLevelError && (
        <p
          className="top-level-error"
          data-testid="sanctions-screening-review-error"
          role="alert"
        >
          {topLevelError}
        </p>
      )}

      <div className="form-bottom">
        <button
          type="button"
          className="back-link"
          data-testid="sanctions-screening-back-to-address-link"
          onClick={() => setCurrentPage(2)}
          aria-label={translate('Back to home address')}
        >
          <SvgChevronLeft
            viewBox="0 0 24 24"
            width={18}
            height={18}
            aria-hidden="true"
          />
          {translate('Home Address')}
        </button>
        <Button
          data-testid="sanctions-screening-confirm-submit-btn"
          onClick={handleSubmit}
          loading={isLoading}
        >
          {translate('Confirm and Submit')}
        </Button>
      </div>
      {renderInfoSafe()}
      <p className="review-disclaimer">
        {translate(
          "We'll use this information to verify your identity, a crucial step in protecting both you and the primary card holder.",
        )}
      </p>
    </div>
  )

  return (
    <div
      data-testid="sanctions-screening-popup"
      className={cn('SanctionedScreeningForm')}
      css={style}
    >
      {renderScreeningImage()}
      {currentPage === 0 && renderNamePage()}
      {currentPage === 1 && renderBirthdayPage()}
      {currentPage === 2 && renderAddressPage()}
      {currentPage === 3 && renderReviewPage()}
    </div>
  )
}
